import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, XCircle, Mail, Phone, Calendar, CreditCard, ExternalLink, RefreshCw, LogOut, CalendarDays, Copy, Clock, Info, Star, MessageSquare, Trash2 } from 'lucide-react';
import { getServiceById, formatPriceRange } from '../data/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [activeTab, setActiveTab] = useState('quotes');
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  // Check authentication
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('adminAuthenticated');
    if (!isAuthenticated) {
      navigate('/admin-login');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote.id);
    setEditAmount(quote.quoteAmount?.toString() || '');
  };

  const handleSaveEdit = async (quoteId) => {
    if (!editAmount || parseFloat(editAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await axios.patch(`${API}/quotes/${quoteId}`, {
        quoteAmount: parseFloat(editAmount)
      });
      
      toast.success('Quote amount updated successfully!');
      setEditingQuote(null);
      setEditAmount('');
      fetchQuotes();
    } catch (error) {
      console.error('Error updating quote:', error);
      toast.error('Failed to update quote amount');
    }
  };

  const handleCancelEdit = () => {
    setEditingQuote(null);
    setEditAmount('');
  };

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
    fetchBookings();
    fetchReviews();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleCopyBookingLink = async (quote) => {
    const bookingUrl = `${window.location.origin}/booking?quoteId=${quote.id}`;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      toast.success('Booking link copied!', { description: 'Send this to the customer' });
    } catch {
      toast.success('Booking link:', { description: bookingUrl, duration: 10000 });
    }
  };

  const handleUpdateBooking = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status });
      toast.success(`Booking ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleDeleteQuote = async (quoteId, quoteName) => {
    if (!window.confirm(`Are you sure you want to delete the quote from ${quoteName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      toast.success('Quote deleted');
      fetchQuotes();
      fetchBookings();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleCleanupOldQuotes = async () => {
    if (!window.confirm('This will delete all rejected quotes and quotes older than 2 weeks that are still pending or approved. Continue?')) return;
    try {
      const res = await axios.post(`${API}/quotes/cleanup`);
      toast.success(`Cleaned up ${res.data.deleted} old quotes`);
      fetchQuotes();
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cleanup quotes');
    }
  };

  const fetchReviews = async () => {
    try {
      setLoadingReviews(true);
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleUpdateReview = async (reviewId, status) => {
    try {
      await axios.patch(`${API}/reviews/${reviewId}`, { status });
      toast.success(`Review ${status}`);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleApprove = async (quoteId) => {
    if (!quoteAmount || parseFloat(quoteAmount) <= 0) {
      toast.error('Please enter a valid quote amount');
      return;
    }

    try {
      await axios.patch(`${API}/quotes/${quoteId}`, {
        status: 'approved',
        quoteAmount: parseFloat(quoteAmount)
      });
      
      toast.success('Quote approved successfully!');
      setSelectedQuote(null);
      setQuoteAmount('');
      fetchQuotes();
    } catch (error) {
      console.error('Error approving quote:', error);
      toast.error('Failed to approve quote');
    }
  };

  const handleReject = async (quoteId) => {
    try {
      await axios.patch(`${API}/quotes/${quoteId}`, {
        status: 'rejected'
      });
      
      toast.success('Quote rejected');
      setSelectedQuote(null);
      fetchQuotes();
    } catch (error) {
      console.error('Error rejecting quote:', error);
      toast.error('Failed to reject quote');
    }
  };

  const handleGeneratePaymentLink = async (quote) => {
    if (quote.status !== 'approved' || !quote.quoteAmount) {
      toast.error('Quote must be approved with an amount first');
      return;
    }

    setProcessingPayment(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/create-checkout`, {
        quoteId: quote.id,
        originUrl: originUrl
      });

      const paymentUrl = response.data.url;
      
      // Try to copy to clipboard (may fail in some browser contexts)
      try {
        await navigator.clipboard.writeText(paymentUrl);
        toast.success('Payment link copied to clipboard!', {
          description: 'You can now send this link to the customer'
        });
      } catch (clipboardErr) {
        toast.success('Payment link generated!', {
          description: paymentUrl,
          duration: 10000
        });
      }
      
      // Open in new tab
      window.open(paymentUrl, '_blank');
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error('Failed to generate payment link', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', label: 'Pending' },
      approved: { variant: 'default', label: 'Approved', className: 'bg-emerald-600' },
      rejected: { variant: 'destructive', label: 'Rejected' },
      paid: { variant: 'default', label: 'Paid', className: 'bg-sky-600' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50">
        <Card className="p-8">
          <RefreshCw className="h-8 w-8 animate-spin text-sky-600 mx-auto" />
          <p className="text-slate-600 mt-4">Loading quotes...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-emerald-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-slate-600">Manage quote requests and payments</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-sky-600">{quotes.filter(q => q.status === 'pending').length}</p>
                <p className="text-sm text-slate-600 mt-1">Pending Quotes</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-600">{quotes.filter(q => q.status === 'approved').length}</p>
                <p className="text-sm text-slate-600 mt-1">Approved Quotes</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{bookings.filter(b => b.status === 'confirmed').length}</p>
                <p className="text-sm text-slate-600 mt-1">Confirmed Bookings</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">{quotes.length}</p>
                <p className="text-sm text-slate-600 mt-1">Total Quotes</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('quotes')}
            variant={activeTab === 'quotes' ? 'default' : 'outline'}
            className={activeTab === 'quotes' ? 'bg-sky-600 hover:bg-sky-700' : ''}
            data-testid="tab-quotes"
          >
            Quote Requests
          </Button>
          <Button
            onClick={() => { setActiveTab('bookings'); fetchBookings(); }}
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            className={activeTab === 'bookings' ? 'bg-sky-600 hover:bg-sky-700' : ''}
            data-testid="tab-bookings"
          >
            <CalendarDays className="h-4 w-4 mr-2" />
            Bookings ({bookings.length})
          </Button>
          <Button
            onClick={() => { setActiveTab('reviews'); fetchReviews(); }}
            variant={activeTab === 'reviews' ? 'default' : 'outline'}
            className={activeTab === 'reviews' ? 'bg-sky-600 hover:bg-sky-700' : ''}
            data-testid="tab-reviews"
          >
            <Star className="h-4 w-4 mr-2" />
            Reviews ({reviews.filter(r => r.status === 'pending').length} pending)
          </Button>
        </div>

        {activeTab === 'quotes' && (
        <>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-slate-900">Quote Requests</h2>
          <div className="flex gap-2">
            <Button onClick={handleCleanupOldQuotes} variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="cleanup-quotes-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Cleanup Old
            </Button>
            <Button onClick={fetchQuotes} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {quotes.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-slate-600">No quote requests yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {quotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{quote.name}</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        {new Date(quote.createdAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="flex gap-2 items-center">
                      {getStatusBadge(quote.status)}
                      {quote.quoteAmount && (
                        <Badge variant="outline" className="bg-white">
                          £{quote.quoteAmount.toFixed(2)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-sky-600" />
                        <span className="text-slate-600">{quote.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-sky-600" />
                        <span className="text-slate-600">{quote.phone}</span>
                      </div>
                      {quote.preferredDate && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-sky-600" />
                          <span className="text-slate-600">{quote.preferredDate}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 mb-1">Service Type:</p>
                      <p className="text-sm text-slate-600 capitalize">{quote.serviceType.replace('-', ' ')}</p>
                      {quote.message && (
                        <>
                          <p className="text-sm font-semibold text-slate-800 mt-3 mb-1">Message:</p>
                          <p className="text-sm text-slate-600">{quote.message}</p>
                        </>
                      )}
                    </div>
                  </div>

                  {selectedQuote === quote.id && quote.status === 'pending' && (
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      {(() => {
                        const svc = getServiceById(quote.serviceType);
                        if (svc) {
                          return (
                            <div className="flex items-start gap-2 mb-3 bg-sky-50 border border-sky-200 rounded-md p-3">
                              <Info className="h-4 w-4 text-sky-600 mt-0.5 flex-shrink-0" />
                              <div className="text-sm">
                                <span className="font-semibold text-slate-800">Suggested range: </span>
                                <Badge className="bg-sky-600 ml-1">{formatPriceRange(svc)}</Badge>
                                <span className="text-slate-500 ml-2">({svc.unit})</span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <Label htmlFor="amount">Quote Amount (£)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="150.00"
                        value={quoteAmount}
                        onChange={(e) => setQuoteAmount(e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  )}

                  {editingQuote === quote.id && (quote.status === 'approved' || quote.status === 'paid') && (
                    <div className="bg-sky-50 rounded-lg p-4 mb-4">
                      <Label htmlFor="editAmount">Edit Quote Amount (£)</Label>
                      <Input
                        id="editAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="150.00"
                        value={editAmount}
                        onChange={(e) => setEditAmount(e.target.value)}
                        className="mt-2"
                      />
                      <div className="flex gap-2 mt-3">
                        <Button
                          onClick={() => handleSaveEdit(quote.id)}
                          className="bg-sky-600 hover:bg-sky-700"
                          size="sm"
                        >
                          Save Changes
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          variant="outline"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    {quote.status === 'pending' && (
                      selectedQuote === quote.id ? (
                        <>
                          <Button
                            onClick={() => handleApprove(quote.id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Confirm Approval
                          </Button>
                          <Button
                            onClick={() => setSelectedQuote(null)}
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => setSelectedQuote(quote.id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve & Set Price
                          </Button>
                          <Button
                            onClick={() => handleReject(quote.id)}
                            variant="destructive"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </>
                      )
                    )}

                    {quote.status === 'approved' && (
                      <>
                        <Button
                          onClick={() => handleGeneratePaymentLink(quote)}
                          disabled={processingPayment}
                          className="bg-sky-600 hover:bg-sky-700"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          {processingPayment ? 'Generating...' : 'Generate Payment Link'}
                        </Button>
                        <Button
                          onClick={() => handleCopyBookingLink(quote)}
                          variant="outline"
                          className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                          data-testid="copy-booking-link"
                        >
                          <CalendarDays className="h-4 w-4 mr-2" />
                          Send Booking Link
                        </Button>
                        <Button
                          onClick={() => handleEditQuote(quote)}
                          variant="outline"
                          className="border-sky-600 text-sky-600 hover:bg-sky-50"
                        >
                          Edit Amount
                        </Button>
                      </>
                    )}

                    {quote.status === 'paid' && (
                      <>
                        <Badge variant="default" className="bg-emerald-600 text-white px-4 py-2">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Payment Completed
                        </Badge>
                        <Button
                          onClick={() => handleEditQuote(quote)}
                          variant="outline"
                          size="sm"
                          className="border-sky-600 text-sky-600 hover:bg-sky-50"
                        >
                          Edit Amount
                        </Button>
                      </>
                    )}

                    <Button
                      onClick={() => handleDeleteQuote(quote.id, quote.name)}
                      variant="outline"
                      size="sm"
                      className="text-red-500 border-red-300 hover:bg-red-50 ml-auto"
                      data-testid="delete-quote-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-slate-900">Bookings</h2>
            <Button onClick={fetchBookings} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {bookings.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <CalendarDays className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-600">No bookings yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id} className="hover:shadow-lg transition-shadow" data-testid={`booking-${booking.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">{booking.customerName}</CardTitle>
                        <p className="text-sm text-slate-500 capitalize mt-1">{booking.serviceType?.replace('-', ' ')}</p>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Badge className={
                          booking.status === 'confirmed' ? 'bg-emerald-600' :
                          booking.status === 'completed' ? 'bg-sky-600' :
                          booking.status === 'cancelled' ? 'bg-red-500' :
                          'bg-amber-500'
                        }>
                          {booking.status}
                        </Badge>
                        {booking.depositPaid && (
                          <Badge variant="outline" className="border-emerald-600 text-emerald-600">Deposit Paid</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-sky-600" />
                          <span className="text-slate-700 font-medium">{booking.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-sky-600" />
                          <span className="text-slate-700">{booking.timeSlot?.replace('-', ' - ')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-sky-600" />
                          <span className="text-slate-600">{booking.customerEmail}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-sky-600" />
                          <span className="text-slate-600">{booking.customerPhone}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Total Amount</span>
                          <span className="font-semibold">£{booking.totalAmount?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Deposit</span>
                          <span className={`font-semibold ${booking.depositPaid ? 'text-emerald-600' : 'text-amber-600'}`}>
                            £{booking.depositAmount?.toFixed(2)} {booking.depositPaid ? '(paid)' : '(unpaid)'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Remaining</span>
                          <span className="font-semibold">£{(booking.totalAmount - booking.depositAmount).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {booking.status === 'confirmed' && (
                        <Button
                          onClick={() => handleUpdateBooking(booking.id, 'completed')}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          size="sm"
                          data-testid="complete-booking-btn"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mark Completed
                        </Button>
                      )}
                      {(booking.status === 'pending' || booking.status === 'confirmed') && (
                        <Button
                          onClick={() => handleUpdateBooking(booking.id, 'cancelled')}
                          variant="destructive"
                          size="sm"
                          data-testid="cancel-booking-btn"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-slate-900">Reviews</h2>
            <Button onClick={fetchReviews} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {reviews.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-600">No reviews yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id} className="hover:shadow-lg transition-shadow" data-testid={`review-admin-${review.id}`}>
                  <CardContent className="py-5">
                    <div className="flex flex-col sm:flex-row justify-between gap-3 mb-3">
                      <div>
                        <p className="font-semibold text-slate-900">{review.name}</p>
                        <p className="text-sm text-slate-500">{review.email}</p>
                        <p className="text-xs text-slate-400 capitalize mt-1">{review.serviceType?.replace('-', ' ')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`h-4 w-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                        <Badge className={
                          review.status === 'approved' ? 'bg-emerald-600' :
                          review.status === 'rejected' ? 'bg-red-500' :
                          'bg-amber-500'
                        }>
                          {review.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-md p-3 mb-3">{review.text}</p>
                    <div className="flex gap-2">
                      {review.status === 'pending' && (
                        <>
                          <Button onClick={() => handleUpdateReview(review.id, 'approved')} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-review-btn">
                            <CheckCircle className="h-4 w-4 mr-2" /> Approve
                          </Button>
                          <Button onClick={() => handleUpdateReview(review.id, 'rejected')} size="sm" variant="destructive" data-testid="reject-review-btn">
                            <XCircle className="h-4 w-4 mr-2" /> Reject
                          </Button>
                        </>
                      )}
                      {review.status === 'approved' && (
                        <Button onClick={() => handleUpdateReview(review.id, 'rejected')} size="sm" variant="outline" className="text-red-600 border-red-300">
                          <XCircle className="h-4 w-4 mr-2" /> Remove
                        </Button>
                      )}
                      {review.status === 'rejected' && (
                        <Button onClick={() => handleUpdateReview(review.id, 'approved')} size="sm" variant="outline" className="text-emerald-600 border-emerald-300">
                          <CheckCircle className="h-4 w-4 mr-2" /> Approve
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
        )}
      </div>
    </div>
  );
};

export default Admin;